// speech-to-speech のエンジン (2.1 と live) で事前チェックを回す画面 (spec 2026-08-29 段階 2)。
//
// **画面は 2.1 と live で共有する** (spec 2026-09-14 L2) — エンジンは useEngine で受け、
// 画面は step の操作 (narrate / askName / promptSilence) を呼ぶだけで instructions も tools も知らない。
//
// **既存の Preflight は触らない。** step の構成 (許可 → スピーカー → 氏名 →
// チュートリアル → 完了) と読み上げる文言は cascade と揃えてあり、違うのは
// 「誰が喋るか」と「氏名をどう取るか」だけ。比較の対象をエンジンの差に絞るため。
//
// NameCapture (1,498 行) のうち作らないもの: 判定層 / 第 2 経路 / 順位付け /
// 収束不能の検出 / 表記の 2 択 / 逐次確認。**残すのはキーボード退避だけ** (原則 5)。
// 復唱確認はモデルに任せる (2026-08-30 のユーザー判断、instructions に書いてある)。

import { useCallback, useEffect, useRef, useState } from "react";
import { MicButton } from "./MicButton";
import { PreflightStage } from "./preflight/PreflightStage";
import { mediaStreamManager } from "../audio/mediaStreamManager";
import { eventBus } from "../observability/eventBus";
import { makeEvent } from "../observability/eventSchema";
import { useChatStore } from "../state/chatStore";
import { formatName } from "../lib/nameNormalize";
import { useRealtimeEngine } from "../hooks/useRealtimeEngine";
import {
  READY_LINES,
  SPEAKER_CHECK_LINES,
  TUTORIAL_LINES,
} from "../conversation/preflightInstructions";
import type { SpeechEngine, UseSpeechEngine } from "../conversation/engine";
import type { PreflightResult } from "./Preflight";

// cascade の MAX_UNCLEAR と同じ考え方。声だけで詰まらせない (原則 5)。
const MAX_NAME_REPEATS = 3;

type Step = "request-mic" | "mic-denied" | "speaker-check" | "name-input" | "tutorial" | "ready";

type Props = {
  micSupported: boolean;
  userId: string;
  onReady: (result: PreflightResult) => void;
  /**
   * 会話エンジン (既定は 2.1)。**mount の間は同じ hook を渡すこと** (hook の呼び出し順が変わる)。
   * App は URL (?engine=) から 1 度だけ決めて渡す
   */
  useEngine?: UseSpeechEngine;
};

export function RealtimePreflight({ micSupported, onReady, useEngine = useRealtimeEngine }: Props) {
  const [step, setStep] = useState<Step>("request-mic");
  const [keyboard, setKeyboard] = useState(false);
  const [typed, setTyped] = useState("");
  const [micError, setMicError] = useState<string | null>(null);
  // callback から読むので ref で持つ (古い state を見ない)
  const stepRef = useRef<Step>("request-mic");
  const nameRef = useRef<string>("");
  const repeatsRef = useRef(0);
  const engineRef = useRef<SpeechEngine | null>(null);

  /**
   * cascade の Preflight は 18 種の preflight.* を出すが、realtime では**比較に要る
   * 5 種**に絞る (端末 / 許可 / step 遷移 / 氏名の確定 / 完走)。engine は realtime 側
   * だけ載せる (?stt=openai と同じ流儀で、欠落 = cascade)。
   */
  const emitPreflight = useCallback((type: string, payload: Record<string, unknown> = {}) => {
    const { sessionId } = useChatStore.getState();
    if (!sessionId) return;
    // どちらのエンジンで回したか (欠落 = cascade)。live は "live" を名乗る (spec 2026-09-14 L14)
    eventBus.emit(
      makeEvent(type, sessionId, { engine: engineRef.current?.kind ?? "realtime", ...payload })
    );
  }, []);

  const go = useCallback(
    (next: Step) => {
      stepRef.current = next;
      setStep(next);
      emitPreflight("preflight.step_viewed", { step: next });
    },
    [emitPreflight]
  );

  // 端末情報。パイロットの端末別集計で毎回要る (cascade の preflight.started と同じ)。
  useEffect(() => {
    emitPreflight("preflight.started", {
      userAgent: navigator.userAgent,
      viewport: { w: window.innerWidth, h: window.innerHeight },
    });
    // mount 時に 1 度だけ
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const narrate = useCallback((lines: string[]) => {
    engineRef.current?.narrate(lines);
  }, []);

  const askName = useCallback(() => {
    repeatsRef.current = 0;
    engineRef.current?.askName();
  }, []);

  const finishName = useCallback(
    (katakana: string, method: "voice" | "keyboard", via?: "live" | "app") => {
      // cascade の name_captured と同じ軸 (method / 言い直しの回数) で並べる。via は live だけが持つ
      // (氏名の二重の網のどちらの経路で記録したか。live モデルだけで何回通ったかを数える)
      emitPreflight("preflight.name_captured", {
        method,
        repeats: repeatsRef.current,
        ...(via ? { via } : {}),
      });
      nameRef.current = katakana;
      go("tutorial");
      narrate(TUTORIAL_LINES);
    },
    [emitPreflight, go, narrate]
  );

  /**
   * 読み上げるだけの step を 1 つ進める。**契機が 2 つある** — エンジンが読み終わりを
   * 知らせたとき (onNarrationDone。2.1 は narration_done の tool、live は話し終え) と、
   * 知らせないまま黙り込んだとき (下の handleSilence)。どの読み上げが終わったかは step が決める。
   */
  const advanceNarration = useCallback(() => {
    if (stepRef.current === "tutorial") {
      go("ready");
      narrate(READY_LINES);
      return;
    }
    if (stepRef.current === "ready") {
      emitPreflight("preflight.completed", {});
      onReady({ displayName: nameRef.current || undefined });
    }
  }, [emitPreflight, go, narrate, onReady]);

  const handleToolCall = useCallback(
    (
      name: string,
      args: Record<string, unknown>,
      _callId: string,
      meta?: { via?: "live" | "app" }
    ) => {
      if (name !== "confirm_name") return;
      // **氏名 step の外で届いたものでは進めない** — live の遅れて届いた tool や重複した記録で、
      // チュートリアルを読み直したり確定を 2 回数えたりしない (spec 2026-09-14 §3.5)
      if (stepRef.current !== "name-input") return;
      const k = typeof args.katakana === "string" ? args.katakana : "";
      // カタカナへ正規化する (cascade と同じ formatName を通す。漢字が混ざっても落ちる)
      const display = formatName(k, k);
      if (!display) return; // 読みに落とせない。聞き直しはモデルが続ける
      finishName(display, "voice", meta?.via);
    },
    [finishName]
  );

  /**
   * 黙ったまま時間が過ぎた。**声を待っている step でだけ促す** —
   * 読み上げるだけの step (チュートリアル・完了案内) で促すと、案内の途中に
   * 「聞こえていますでしょうか」が挟まる。realtime には無音の出口が構造的に無いので、
   * 続けて黙るならキーボードへ倒す (原則 5)。
   */
  const handleSilence = useCallback(
    (level: number) => {
      /**
       * **読み上げるだけの step には出口が要る。** narration_done で進む設計だが、
       * モデルは指定した文を読まずに「では、順番に読み上げますね。」だけ言って
       * 終わることがあり、**その回は tool も飛ばない** (実測 2026-09-02、
       * docs/measurements/2026-09-02-realtime-narration-skip.md)。この画面には
       * ボタンが 1 つも無いので、ここが無いと永久に止まる (実機で 156 秒の沈黙)。
       * 促さず黙って進めるのは、読み終えている可能性が高く「聞こえていますか」を
       * 挟むと案内の途中に割り込む形になるため。
       */
      if (stepRef.current === "tutorial" || stepRef.current === "ready") {
        advanceNarration();
        return;
      }
      if (stepRef.current !== "name-input") return;
      if (level === 0) engineRef.current?.promptSilence();
      else setKeyboard(true);
    },
    [advanceNarration]
  );

  const engine = useEngine({
    onSilence: handleSilence,
    onNarrationDone: advanceNarration,
    onAnswerCommitted: () => {
      /* 事前チェックでは使わない (回答の tool を渡していない) */
    },
    onNeedRepeat: () => {
      repeatsRef.current += 1;
      // 声だけで詰まらせない (原則 5)。上限は engine ではなくこちらが持つ。
      if (repeatsRef.current >= MAX_NAME_REPEATS) setKeyboard(true);
    },
    onError: () => setKeyboard(true),
    onToolCall: handleToolCall,
  });
  engineRef.current = engine;

  const handleRequestMic = useCallback(async () => {
    // **最初の await より前に呼ぶこと。** iOS の autoplay 許可はユーザー操作の
    // コールスタック内でしか得られず、後ろに置くと realtime の声が 1 つも鳴らない
    // (cascade の Preflight が getTtsPlayer().unlock() を先頭で呼ぶのと同じ理由)。
    engineRef.current?.unlock();
    if (!micSupported) {
      setMicError(
        "このブラウザは音声入力に対応していません。PC の Chrome など対応ブラウザで開き直してください。"
      );
      go("mic-denied");
      return;
    }
    try {
      await mediaStreamManager.ensureStream();
      emitPreflight("preflight.mic_permission", { granted: true });
      // live はセッションの作成時に役割が決まる (instructions は起動後に変えられない)
      await engineRef.current?.start({ purpose: "preflight" });
      go("speaker-check");
      narrate(SPEAKER_CHECK_LINES);
    } catch {
      emitPreflight("preflight.mic_permission", { granted: false });
      setMicError(
        "マイクの利用が許可されませんでした。このアンケートは音声でお答えいただくため、マイクの許可をお願いしています。アドレスバーのマイクアイコンから「許可」に変更し、「もう一度試す」を押してください。"
      );
      go("mic-denied");
    }
  }, [emitPreflight, go, micSupported, narrate]);

  useEffect(
    () => () => {
      engineRef.current?.stop();
    },
    []
  );

  const submitTyped = () => {
    const display = formatName(typed, typed);
    if (!display) return;
    finishName(display, "keyboard");
  };

  if (step === "request-mic" || step === "mic-denied") {
    return (
      <div className="mx-auto w-full max-w-2xl flex flex-col flex-1 px-6 py-10 gap-5 items-center text-center">
        <h1 className="text-lg font-semibold text-slate-800">はじめる前の確認</h1>
        <p className="text-sm text-slate-600">
          音声でお答えいただくため、マイクの利用を許可してください。
        </p>
        {micError && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            {micError}
          </p>
        )}
        <button
          type="button"
          onClick={() => void handleRequestMic()}
          className="rounded-full bg-emerald-600 text-white px-6 py-3 text-sm font-medium hover:bg-emerald-700"
        >
          {step === "mic-denied" ? "もう一度試す" : "マイクを許可する"}
        </button>
      </div>
    );
  }

  if (step === "speaker-check") {
    return (
      <div className="mx-auto w-full max-w-2xl flex flex-col flex-1 px-6 py-10 gap-6 items-center text-center">
        <h1 className="text-lg font-semibold text-slate-800">音が聞こえますか</h1>
        <MicButton state="ai-speaking" onStart={() => {}} size="lg" />
        <button
          type="button"
          onClick={() => {
            go("name-input");
            askName();
          }}
          className="rounded-full bg-emerald-600 text-white px-6 py-3 text-sm font-medium hover:bg-emerald-700"
        >
          聞こえました
        </button>
      </div>
    );
  }

  if (step === "name-input") {
    return (
      <div className="mx-auto w-full max-w-2xl flex flex-col flex-1 px-6 py-10 gap-6 items-center text-center">
        <MicButton
          state={engine.state === "user-speaking" ? "speaking" : "listening"}
          onStart={() => {}}
          size="lg"
        />
        {keyboard && (
          <div className="w-full max-w-sm flex gap-2">
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="お名前 (カタカナ)"
              className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
            <button
              type="button"
              onClick={submitTyped}
              disabled={!formatName(typed, typed)}
              className="rounded-lg bg-emerald-600 text-white px-4 py-2 text-sm disabled:bg-slate-200 disabled:text-slate-400"
            >
              決定
            </button>
          </div>
        )}
      </div>
    );
  }

  // tutorial / ready — 読み上げるだけ。本編と同じ画面にして境目で動かさない (D14/D15)
  return <PreflightStage testId={`realtime-preflight-${step}`} />;
}
